import { IReactionDisposer, reaction } from 'mobx';
import { types, Instance } from 'mobx-state-tree';
import InvokerType from './InvokerType';
import { RTModel } from './RTModel';
import config from 'config';

const DailyCOConfig = types.model('DailyCOConfig', {
  url: types.string,
  token: types.string,
});

export interface IDailyCOConfig extends Instance<typeof DailyCOConfig> { }

export const MediaStore = types
  .model('MediaStore', {
    audioMuted: false,
    videoMuted: false,
    audioOutMuted: false,
    noiseCancellationEnabled: false,
    micLocked: false
  })
  .actions((self) => ({
    toggleVideo() {
      self.videoMuted = !self.videoMuted;
    },
    toggleAudio() {
      self.audioMuted = !self.audioMuted;
    },
    setAudioMuted(state: boolean) {
      self.audioMuted = state;
    },
    setVideoMuted(state: boolean) {
      self.videoMuted = state;
    },
    setMicLocked(state: boolean) {
      self.micLocked = state;
    },
    toggleAudioOut() {
      self.audioOutMuted = !self.audioOutMuted;
    },
    toggleNoiseCancellation() {
      self.noiseCancellationEnabled = !self.noiseCancellationEnabled;
    },
  }))
  .actions((self) => {
    let disposerAudio: IReactionDisposer | null = null;
    let disposerVideo: IReactionDisposer | null = null;

    function localize() {
      // Only restore and persist video state when student camera controls are enabled.
      // When disabled, cameras always start on (videoMuted stays false).
      if (config.SHOW_STUDENT_CAMERA_CONTROLS) {
        const storedVideo = window.localStorage.getItem('MediaStore.videoMuted');
        if (storedVideo) self.videoMuted = storedVideo === '1';

        disposerVideo = reaction(
          () => self.videoMuted,
          (muted) => {
            window.localStorage.setItem('MediaStore.videoMuted', muted ? '1' : '0');
          }
        );
      }

      const storedAudio = window.localStorage.getItem('MediaStore.audioMuted');
      if (storedAudio) self.audioMuted = storedAudio === '1';

      disposerAudio = reaction(
        () => self.audioMuted,
        (muted) => {
          window.localStorage.setItem('MediaStore.audioMuted', muted ? '1' : '0');
        }
      );
    }

    function beforeDestroy() {
      disposerVideo && disposerVideo();
      disposerAudio && disposerAudio();
    }
    return { localize, beforeDestroy };
  });

export interface IMediaStore extends Instance<typeof MediaStore> { }

export const CallStore = RTModel.named('CallStore')
  .props({
    mediaStoreMap: types.map(MediaStore),
    roomName: types.maybe(types.string),
    refresh_dailyco_call: InvokerType,
  })
  .actions((self) => ({
    refreshDailyCOCall(create_new = false) {
      return self.refresh_dailyco_call.invoke({ create_new }) as Promise<IDailyCOConfig>;
    },
    setMuteStateForAll(mute: boolean) {
      self.mediaStoreMap.forEach((remoteControl) => {
        remoteControl.setAudioMuted(mute);
      });
    },
    setMicLockStateForAll(lock: boolean, exceptUsernames?: Set<string>) {
      self.mediaStoreMap.forEach((remoteControl, username) => {
        if (exceptUsernames && exceptUsernames.has(username.toString())) return;
        remoteControl.setMicLocked(lock);
        if (lock) {
          remoteControl.setAudioMuted(true);
        }
      });
    }
  }));

export interface ICallStore extends Instance<typeof CallStore> { }
